import { useAuth0 } from "ct-auth"
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useGenerationApis } from "../../data-utils/generationUtil"
import { isManager, canManageChildOrgs, userOrg } from "../../data-utils/authUtils"
import { useActiveManagedOrgs } from "../../data-utils/organizationUtils"
import { startRoleAssumption } from "../../data-utils/roleAssumptionApi"
import { useRoleAssumption } from "../../hooks/useRoleAssumption"
import logger from "../../Logger"
import { UiCtButton } from "../common/MuiStyled/LibComponent"

const pageLogger = logger.child({ module: "RoleAssumptionModal" })

// Roles a manager may assume. Mirrors the server-side whitelist in
// server/utils/role-assumption-handlers.js (ASSUMABLE_ROLES).
const ASSUMABLE_ROLES = [
  { value: "sponsor", label: "Sponsor (org administrator)" },
  { value: "trainer", label: "Trainer (org operator)" },
]

const RoleAssumptionModal = ({ open, onClose }) => {
  const { getAccessTokenSilently, user } = useAuth0()
  const { start: persistAssumption } = useRoleAssumption()

  const [selectedOrg, setSelectedOrg] = useState(null)
  const [selectedRole, setSelectedRole] = useState("sponsor")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // Tracks whether the component is still mounted — prevents "setState
  // on unmounted component" warnings when the user closes the modal
  // mid-request (or navigates away).
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Two caller classes: a chaostrack MANAGER assumes into ANY org; a PARTNER MANAGER (a partner-
  // org sponsor, identified by the manage-child-orgs grant) assumes only into orgs their partner
  // org manages. Pick the applicable org source, and restrict the role for partner managers.
  const isManagerUser = isManager(user)
  const isPartnerManager = !isManagerUser && canManageChildOrgs(user)

  // Managers use the manager-only "all orgs" endpoint; partner managers use their managed fleet.
  const { data: orgsRaw, isLoading: mgrOrgsLoading, error: orgsError } = useGenerationApis.GET_ORGS({ enabled: open && isManagerUser })
  const { activeManagedOrgs, isLoading: fleetLoading } = useActiveManagedOrgs()
  const homeOrgId = userOrg(user)

  const orgs = useMemo(() => {
    const raw = (() => {
      if (isManagerUser) {
        if (!orgsRaw) return []
        if (Array.isArray(orgsRaw)) return orgsRaw
        if (Array.isArray(orgsRaw.results)) return orgsRaw.results
        if (Array.isArray(orgsRaw.data)) return orgsRaw.data
        return []
      }
      // Partner manager: their managed CHILD orgs only — exclude their own partner org (you assume
      // INTO a managed org, not into yourself; the server boundary would reject the home org anyway).
      return (Array.isArray(activeManagedOrgs) ? activeManagedOrgs : []).filter((o) => (o?.id ?? o?._key) !== homeOrgId)
    })()
    // De-dupe by org id. The picker was showing the same org many times (e.g. seven identical
    // "The College of Charleston") because the list could contain repeats; collapse to one entry
    // per id so a duplicated/merged cache can never surface phantom rows.
    const seen = new Set()
    return raw.filter((o) => {
      const key = o?.id ?? o?._key
      if (!key || seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [isManagerUser, orgsRaw, activeManagedOrgs, homeOrgId])
  const orgsLoading = isManagerUser ? mgrOrgsLoading : fleetLoading

  // Partner managers may assume the SPONSOR role only (server enforces this too).
  const roleOptions = isManagerUser ? ASSUMABLE_ROLES : ASSUMABLE_ROLES.filter((r) => r.value === "sponsor")

  const handleClose = useCallback(() => {
    if (submitting) return
    setSelectedOrg(null)
    setSelectedRole("sponsor")
    setError(null)
    onClose?.()
  }, [submitting, onClose])

  const handleStart = useCallback(async () => {
    if (!user?.sub) {
      setError("You must be signed in to assume a role.")
      return
    }
    if (submitting || !selectedOrg) return
    const targetOrgId = selectedOrg.id ?? selectedOrg._key
    if (!targetOrgId) {
      setError("Selected organization is missing an id.")
      return
    }
    if (!selectedRole) {
      setError("Please choose a role to assume.")
      return
    }

    setError(null)
    setSubmitting(true)
    try {
      const accessToken = await getAccessTokenSilently()
      const result = await startRoleAssumption(accessToken, user.sub, {
        targetOrgId,
        targetRole: selectedRole,
      })
      // If the modal unmounted while the request was in flight (e.g. the
      // user navigated away), treat the click as abandoned: skip persist
      // + reload so we don't yank the user back to the home page via a
      // surprise window.location.reload(). The server-side audit row was
      // already written with outcome=success; that's acceptable — they
      // did successfully authorize the assumption, they just didn't
      // wait around to use it.
      if (!isMountedRef.current) return
      // Persist to sessionStorage. setRoleAssumption throws if storage is
      // unavailable or the entry is malformed — we surface the message.
      persistAssumption({
        token: result.token,
        expiresAt: result.expiresAt,
        ttlMinutes: result.ttlMinutes,
        assumed: result.assumed,
      })
      // Hard reload so every consumer of the Auth0 user object re-renders
      // against the new role/org context. sessionStorage survives reload.
      window.location.reload()
    } catch (err) {
      pageLogger.error({ err: err?.message ?? String(err), targetOrgId, targetRole: selectedRole }, "role assumption failed")
      if (isMountedRef.current) {
        setError(err?.message ?? "Failed to start role assumption")
        setSubmitting(false)
      }
    }
  }, [user, submitting, selectedOrg, selectedRole, getAccessTokenSilently, persistAssumption])

  const canSubmit = !!selectedOrg && !!selectedRole && !submitting

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Assume role
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Temporarily act as the chosen role in the chosen organization. Your identity stays the same, only your role and org change. All actions
          are audited.
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Organization
            </Typography>
            <Autocomplete
              size="small"
              options={orgs}
              loading={orgsLoading}
              value={selectedOrg}
              onChange={(_, val) => setSelectedOrg(val)}
              getOptionLabel={(o) => o?.displayName ?? o?.name ?? o?.id ?? ""}
              isOptionEqualToValue={(a, b) => (a?.id ?? a?._key) === (b?.id ?? b?._key)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search organizations..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {orgsLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {orgsError && (
              <Typography variant="caption" color="error">
                Failed to load organizations.
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Role to assume
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="role-assumption-role-label">Role</InputLabel>
              <Select
                labelId="role-assumption-role-label"
                value={selectedRole}
                label="Role"
                disabled={roleOptions.length <= 1}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                {roleOptions.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <UiCtButton variant="outlined" onClick={handleClose} disabled={submitting}>
          Cancel
        </UiCtButton>
        <UiCtButton variant="contained" onClick={handleStart} disabled={!canSubmit}>
          {submitting ? "Starting…" : "Start"}
        </UiCtButton>
      </DialogActions>
    </Dialog>
  )
}

export default RoleAssumptionModal
